

import React, { useState } from 'react';
import { Language } from '../types';
import { TRANSLATIONS } from '../translations';

interface ContactProps {
  language: Language;
}

export const Contact: React.FC<ContactProps> = ({ language }) => {
  const t = TRANSLATIONS[language].contact;
  const [formData, setFormData] = useState({ 
    name: '', 
    email: '', 
    phone: '', 
    ownLand: '', 
    location: '', 
    typeInterested: '', 
    budget: '', 
    havePlans: '', 
    message: '' 
  });
  const [files, setFiles] = useState<File[]>([]);
  const [submitted, setSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const payload = new FormData();
      Object.entries(formData).forEach(([key, value]) => {
        payload.append(key, value);
      });
      
      files.forEach(file => {
        payload.append('files', file);
      });

      const response = await fetch('/api/contact', {
        method: 'POST',
        body: payload,
      });
      
      if (response.ok) {
        setSubmitted(true);
      } else {
        const errorText = await response.text();
        let errorData: any = {};
        try { errorData = JSON.parse(errorText); } catch(e) {}
        alert(`Failed to send message (Status: ${response.status}). Error: ${errorData.error || errorText.substring(0, 500)}`);
      }
    } catch (error: any) {
      console.error("Error submitting form:", error);
      alert(`An error occurred: ${error.message || 'Please try again.'}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const inputClass = "w-full bg-white/5 border border-white/10 p-4 rounded-xl outline-none focus:border-blue-500 transition-colors text-white placeholder:text-slate-600";
  const labelClass = "block text-[10px] font-black uppercase tracking-widest text-slate-500 mb-2";

  return (
    <div className="pt-32 lg:pt-36 bg-slate-50">
      <section className="py-20 lg:py-32">
        <div className="max-w-7xl mx-auto px-4 grid lg:grid-cols-2 gap-20">
          <div>
            <h2 className="text-[10px] font-black text-blue-600 uppercase tracking-[0.4em] mb-12">{t.tag}</h2>
            <h1 className="text-4xl lg:text-5xl font-black text-slate-900 mb-8 tracking-tighter">{t.title}</h1>
            <p className="text-lg text-slate-600 mb-16 leading-relaxed">
              {t.desc}
            </p>

            <div className="space-y-12">
              <div className="flex gap-6 items-start">
                <div className="w-12 h-12 bg-white rounded-2xl flex items-center justify-center text-slate-900 shrink-0 shadow-sm border border-slate-100">
                  <i className="fa-solid fa-phone"></i>
                </div>
                <div>
                  <h4 className="text-[10px] font-black uppercase tracking-widest text-slate-400 mb-2">{t.labels.phone}</h4>
                  <p className="text-2xl font-bold text-slate-900">0432 866 168</p>
                </div>
              </div>

              <div className="flex gap-6 items-start">
                <div className="w-12 h-12 bg-white rounded-2xl flex items-center justify-center text-slate-900 shrink-0 shadow-sm border border-slate-100">
                  <i className="fa-solid fa-envelope"></i>
                </div>
                <div>
                  <h4 className="text-[10px] font-black uppercase tracking-widest text-slate-400 mb-2">{t.labels.email}</h4>
                  <p className="text-2xl font-bold text-slate-900">
                    <a href="mailto:enquiry@lesector.com.au" className="hover:text-blue-600 transition-colors">enquiry@lesector.com.au</a>
                  </p>
                </div>
              </div>

              <div className="flex gap-6 items-start">
                <div className="w-12 h-12 bg-white rounded-2xl flex items-center justify-center text-slate-900 shrink-0 shadow-sm border border-slate-100">
                  <i className="fa-solid fa-location-dot"></i>
                </div>
                <div>
                  <h4 className="text-[10px] font-black uppercase tracking-widest text-slate-400 mb-2">{t.labels.area}</h4>
                  <p className="text-2xl font-bold text-slate-900">Melbourne & Victoria</p>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-slate-900 p-8 lg:p-12 rounded-[2rem] text-white shadow-2xl">
             <h3 className="text-2xl font-black mb-8">{t.labels.formTitle}</h3>
             
             {submitted ? (
               <div className="animate-in fade-in zoom-in duration-500 text-center py-10">
                 <div className="w-16 h-16 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg shadow-green-500/30">
                   <i className="fa-solid fa-check text-white text-2xl"></i>
                 </div>
                 <h4 className="text-xl font-bold mb-3">Message Sent</h4>
                 <p className="text-slate-400 text-sm mb-8 leading-relaxed">
                   Thank you for reaching out. We have received your enquiry and an automatic confirmation email has been sent. Our team will contact you shortly.
                 </p>
                 <button 
                  onClick={() => { 
                    setSubmitted(false); 
                    setFiles([]);
                    setFormData({ name: '', email: '', phone: '', ownLand: '', location: '', typeInterested: '', budget: '', havePlans: '', message: '' }); 
                  }} 
                  className="text-[10px] font-black uppercase tracking-widest text-blue-400 hover:text-white transition-colors"
                 >
                   Send Another Request
                 </button>
               </div>
             ) : (
               <form className="space-y-5" onSubmit={handleSubmit}>
                  <div className="grid md:grid-cols-2 gap-5">
                    <div>
                      <label className={labelClass}>{t.labels.name} *</label>
                      <input 
                        required
                        value={formData.name}
                        onChange={(e) => setFormData({...formData, name: e.target.value})}
                        className={inputClass} 
                        placeholder="E.g. Michael Chen" 
                      />
                    </div>
                    <div>
                      <label className={labelClass}>{t.labels.emailLabel} *</label>
                      <input 
                        required
                        type="email"
                        value={formData.email}
                        onChange={(e) => setFormData({...formData, email: e.target.value})}
                        className={inputClass} 
                        placeholder="michael@example.com" 
                      />
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-5">
                    <div>
                      <label className={labelClass}>{t.labels.phoneLabel} *</label>
                      <input 
                        required
                        type="tel"
                        value={formData.phone}
                        onChange={(e) => setFormData({...formData, phone: e.target.value})}
                        className={inputClass} 
                        placeholder="0400 000 000" 
                      />
                    </div>
                    <div>
                      <label className={labelClass}>{t.labels.location}</label>
                      <input 
                        value={formData.location}
                        onChange={(e) => setFormData({...formData, location: e.target.value})}
                        className={inputClass} 
                        placeholder="E.g. Richmond, VIC" 
                      />
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-5">
                    <div>
                      <label className={labelClass}>{t.labels.ownLand}</label>
                      <select 
                        value={formData.ownLand}
                        onChange={(e) => setFormData({...formData, ownLand: e.target.value})}
                        className={`${inputClass} appearance-none`}
                      >
                        <option value="" disabled className="text-slate-500">Please select</option>
                        <option value="Yes" className="text-slate-900">Yes</option>
                        <option value="No, searching" className="text-slate-900">No, searching</option>
                        <option value="No, need assistance" className="text-slate-900">No, need assistance</option>
                      </select>
                    </div>
                    <div>
                      <label className={labelClass}>{t.labels.havePlans}</label>
                      <select 
                        value={formData.havePlans}
                        onChange={(e) => setFormData({...formData, havePlans: e.target.value})}
                        className={`${inputClass} appearance-none`}
                      >
                        <option value="" disabled className="text-slate-500">Please select</option>
                        <option value="Yes, approved" className="text-slate-900">Yes, town planning approved</option>
                        <option value="Yes, working drawings" className="text-slate-900">Yes, working drawings</option>
                        <option value="No, starting raw" className="text-slate-900">No, need design</option>
                      </select>
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-5">
                    <div>
                      <label className={labelClass}>{t.labels.typeInterested}</label>
                      <select 
                        value={formData.typeInterested}
                        onChange={(e) => setFormData({...formData, typeInterested: e.target.value})}
                        className={`${inputClass} appearance-none`}
                      >
                        <option value="" disabled className="text-slate-500">Please select</option>
                        <option value="Single House" className="text-slate-900">Single House</option>
                        <option value="Multi-Unit / Townhouse" className="text-slate-900">Multi-Unit / Townhouse</option>
                        <option value="Granny Flat / Studio" className="text-slate-900">Granny Flat / Studio</option>
                        <option value="Other" className="text-slate-900">Other</option>
                      </select>
                    </div>
                    <div>
                      <label className={labelClass}>{t.labels.budget}</label>
                      <select 
                        value={formData.budget}
                        onChange={(e) => setFormData({...formData, budget: e.target.value})}
                        className={`${inputClass} appearance-none`}
                      >
                        <option value="" disabled className="text-slate-500">Please select</option>
                        <option value="Under $500k" className="text-slate-900">Under $500k</option>
                        <option value="$500k - $800k" className="text-slate-900">$500k - $800k</option>
                        <option value="$800k - $1.2M" className="text-slate-900">$800k - $1.2M</option>
                        <option value="$1.2M+" className="text-slate-900">$1.2M+</option>
                      </select>
                    </div>
                  </div>

                  <div>
                    <label className={labelClass}>{t.labels.message} *</label>
                    <textarea 
                      required
                      value={formData.message}
                      onChange={(e) => setFormData({...formData, message: e.target.value})}
                      className={`${inputClass} h-24 resize-none`} 
                      placeholder="Briefly describe your project..." 
                    />
                  </div>
                  
                  <div>
                    <label className={labelClass}>{t.labels.fileUpload || 'Upload Site Plans, Drawings or Supporting Documents (Optional)'}</label>
                    <input
                      type="file"
                      multiple
                      accept=".pdf,.jpg,.jpeg,.png"
                      onChange={(e) => setFiles(e.target.files ? Array.from(e.target.files) : [])}
                      className="w-full text-sm text-slate-500 file:mr-4 file:py-3 file:px-4 file:rounded-full file:border-0 file:text-xs file:font-semibold file:bg-white/10 file:text-white hover:file:bg-white/20 transition-all cursor-pointer"
                    />
                    {files.length > 0 && (
                      <div className="mt-2 text-xs text-slate-400">
                        {files.length} file(s) selected
                      </div>
                    )}
                  </div>
                  
                  <button 
                    type="submit" 
                    disabled={isSubmitting}
                    className="w-full py-5 bg-blue-600 text-white font-black uppercase tracking-[0.2em] text-[10px] rounded-xl hover:bg-blue-500 transition-all shadow-xl shadow-blue-600/20 disabled:opacity-50 disabled:cursor-not-allowed mt-4"
                  >
                    {isSubmitting ? 'Sending...' : t.labels.submit}
                  </button>
               </form>
             )}
          </div>
        </div>
      </section>
    </div>
  );
};
